import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Spinner,
  Badge,
  Divider,
} from "@chakra-ui/react"
import {
  companyRatecardMapping,
  getAllRateCard,
  getCompanyAllMappings,
  getCompanyRateCards,
  deleteCompanyRateCardMapping,
  setDefaultRateCardMapping,
} from "../../Http/Index"
import { ShowAlert } from "../../GlobalComponent/Alerts/Alert"
import CompanySelector from "../../GlobalComponent/companySelector/CompanySelector"

function RateCardMapping({ clearFormFour, setClearFormFour }: { clearFormFour: boolean; setClearFormFour: any }) {
  // Section 1
  const [companyId, setCompanyId] = useState("")
  const [companyName, setCompanyName] = useState("")
  const [allRateCards, setAllRateCards] = useState<any[]>([])
  const [rateCardId, setRateCardId] = useState("")
  const [hasPlanId, setHasPlanId] = useState(false)
  const [planId, setPlanId] = useState("")
  const [isGMC, setIsGMC] = useState(false)
  const [minCount, setMinCount] = useState("")
  const [maxCount, setMaxCount] = useState("")
  const [countScope, setCountScope] = useState("")
  const [loading, setLoading] = useState(false)
  const [companyMappings, setCompanyMappings] = useState<any[]>([])
  const [mappingsLoading, setMappingsLoading] = useState(false)

  // Section 2
  const [defaultRateCardId, setDefaultRateCardId] = useState("")
  const [defaultLoading, setDefaultLoading] = useState(false)
  const [defaultMappings, setDefaultMappings] = useState<any[]>([])
  const [defaultMappingsLoading, setDefaultMappingsLoading] = useState(false)

  useEffect(() => {
    fetchAllRateCards()
    fetchDefaultMappings()
    //eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (companyId) fetchCompanyMappings()
    else setCompanyMappings([])
    //eslint-disable-next-line
  }, [companyId])

  useEffect(() => {
    if (clearFormFour) {
      handleClear()
      setClearFormFour(false)
    }
    //eslint-disable-next-line
  }, [clearFormFour])

  const fetchAllRateCards = async () => {
    try {
      const res = await getAllRateCard()
      setAllRateCards(res?.data?.data || res?.data || [])
    } catch (e) {
      console.error(e)
    }
  }

  const fetchCompanyMappings = async () => {
    try {
      setMappingsLoading(true)
      const res = await getCompanyAllMappings(companyId)
      setCompanyMappings(res?.data?.data || [])
    } catch (e: any) {
      if (e?.response?.status !== 404) console.error(e)
      setCompanyMappings([])
    } finally {
      setMappingsLoading(false)
    }
  }

  const fetchDefaultMappings = async () => {
    try {
      setDefaultMappingsLoading(true)
      const res = await getCompanyRateCards("DEFAULT")
      setDefaultMappings(res?.data?.data || [])
    } catch (e: any) {
      if (e?.response?.status !== 404) console.error(e)
      setDefaultMappings([])
    } finally {
      setDefaultMappingsLoading(false)
    }
  }

  const handleClear = () => {
    setCompanyId("")
    setCompanyName("")
    setRateCardId("")
    setHasPlanId(false)
    setPlanId("")
    setIsGMC(false)
    setMinCount("")
    setMaxCount("")
    setCountScope("")
    setCompanyMappings([])
  }

  const handleSubmit = async () => {
    if (!companyId) return ShowAlert("warning", "Select a company")
    if (!rateCardId) return ShowAlert("warning", "Select a rate card")
    if (isGMC && hasPlanId) return ShowAlert("warning", "Cannot set both GMC and Plan ID on the same mapping")
    if (hasPlanId && !planId.trim()) return ShowAlert("warning", "Enter a Plan ID")
    if (minCount && (isNaN(Number(minCount)) || Number(minCount) < 0)) {
      return ShowAlert("warning", "Min Count must be a number >= 0")
    }
    if (maxCount && (isNaN(Number(maxCount)) || Number(maxCount) < 1)) {
      return ShowAlert("warning", "Max Count must be a number >= 1")
    }
    try {
      setLoading(true)
      const payload: any = { companyId, rateCardId }
      if (hasPlanId && planId.trim()) payload.planId = planId.trim()
      if (isGMC) payload.isGMC = true
      if (minCount !== "") payload.minCount = Number(minCount)
      if (maxCount !== "") payload.maxCount = Number(maxCount)
      if (countScope) payload.countScope = countScope
      await companyRatecardMapping(payload)
      ShowAlert("success", "Rate card mapped successfully")
      setRateCardId("")
      setHasPlanId(false)
      setPlanId("")
      setIsGMC(false)
      setMinCount("")
      setMaxCount("")
      setCountScope("")
      fetchCompanyMappings()
    } catch (e: any) {
      ShowAlert("warning", e?.response?.data?.message || "Error saving mapping")
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    const mapping = companyMappings.find((m: any) => m._id === id)
    if (mapping?.isDefaultMapping) {
      return ShowAlert("warning", "Cannot delete the default mapping")
    }
    try {
      await deleteCompanyRateCardMapping(id)
      ShowAlert("success", "Mapping deleted successfully")
      fetchCompanyMappings()
    } catch (e: any) {
      ShowAlert("warning", e?.response?.data?.message || "Error deleting mapping")
    }
  }

  const handleSetDefault = async () => {
    if (!defaultRateCardId) return ShowAlert("warning", "Select a rate card")
    try {
      setDefaultLoading(true)
      await setDefaultRateCardMapping({ rateCardId: defaultRateCardId })
      ShowAlert("success", "Default rate card set successfully")
      setDefaultRateCardId("")
      fetchDefaultMappings()
    } catch (e: any) {
      ShowAlert("warning", e?.response?.data?.message || "Error setting default rate card")
    } finally {
      setDefaultLoading(false)
    }
  }

  const handleDeleteDefault = async (id: string) => {
    try {
      await deleteCompanyRateCardMapping(id)
      ShowAlert("success", "Default mapping deleted")
      fetchDefaultMappings()
    } catch (e: any) {
      ShowAlert("warning", e?.response?.data?.message || "Error deleting default mapping")
    }
  }

  const rateCardOptions = allRateCards.map((rc: any) => (
    <option key={rc._id} value={rc._id}>{rc.rateCardName}</option>
  ))

  return (
    <Box display="flex" flexDirection="column" w="100%" gap={8}>

      {/* Section 1: Rate Card Mapping */}
      <Box display="flex" w="100%" gap={6} alignItems="flex-start">
        <Box p={6} minW="320px" maxW="360px" bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="bold" mb={5} color="gray.700">Rate Card Mapping</Text>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Select Company</FormLabel>
              <CompanySelector
                selectedCompany={companyName}
                onCompanySelect={(id, name) => { setCompanyId(id); setCompanyName(name) }}
                label="" width="100%" placeholder="Select a company"
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Rate Card</FormLabel>
              <Select
                placeholder="Select Rate Card" value={rateCardId}
                onChange={(e) => setRateCardId(e.target.value)}
                borderColor="gray.300" _hover={{ borderColor: "teal.400" }} focusBorderColor="teal.400"
              >
                {rateCardOptions}
              </Select>
            </FormControl>
            <FormControl>
              <Checkbox
                isChecked={hasPlanId}
                onChange={(e) => { setHasPlanId(e.target.checked); if (!e.target.checked) setPlanId(""); if (e.target.checked) setIsGMC(false) }}
                colorScheme="teal"
                isDisabled={isGMC}
              >
                <Text fontSize="sm" color="gray.600">Add Plan ID</Text>
              </Checkbox>
              {hasPlanId && (
                <Input
                  mt={2}
                  size="sm"
                  placeholder="Enter Plan ID"
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                  borderColor="gray.300"
                  _hover={{ borderColor: "teal.400" }}
                  focusBorderColor="teal.400"
                />
              )}
            </FormControl>
            <FormControl>
              <Checkbox
                isChecked={isGMC}
                onChange={(e) => { setIsGMC(e.target.checked); if (e.target.checked) { setHasPlanId(false); setPlanId("") } }}
                colorScheme="teal"
                isDisabled={hasPlanId}
              >
                <Text fontSize="sm" color="gray.600">GMC</Text>
              </Checkbox>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Min Count (optional)</FormLabel>
              <Input
                type="number"
                size="sm"
                placeholder="e.g., 0"
                value={minCount}
                onChange={(e) => setMinCount(e.target.value)}
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                focusBorderColor="teal.400"
                min={0}
              />
              <Text fontSize="xs" color="gray.400" mt={1}>Card applies when ambulance usage is &gt;= this value</Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Max Count (optional)</FormLabel>
              <Input
                type="number"
                size="sm"
                placeholder="e.g., 2 (leave empty for no upper limit)"
                value={maxCount}
                onChange={(e) => setMaxCount(e.target.value)}
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                focusBorderColor="teal.400"
                min={1}
              />
              <Text fontSize="xs" color="gray.400" mt={1}>Card applies when ambulance usage is &lt; this value. Empty = no limit.</Text>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Count Scope (optional)</FormLabel>
              <Select
                size="sm"
                placeholder="Select scope"
                value={countScope}
                onChange={(e) => setCountScope(e.target.value)}
                borderColor="gray.300"
                _hover={{ borderColor: "teal.400" }}
                focusBorderColor="teal.400"
              >
                <option value="beneficiary">Beneficiary (per person)</option>
                <option value="subscriber">Subscriber (per account)</option>
              </Select>
              <Text fontSize="xs" color="gray.400" mt={1}>Which ambulance count to compare against</Text>
            </FormControl>
            <HStack pt={2}>
              <Button colorScheme="teal" onClick={handleSubmit} isLoading={loading} flex={1}>Save Mapping</Button>
              <Button variant="outline" onClick={handleClear}>Clear</Button>
            </HStack>
          </VStack>
        </Box>

        <Box flex={1} minW={0}>
          <Flex align="center" justify="space-between" mb={4}>
            <Text fontWeight="bold" fontSize="lg" color="gray.700">
              {companyName ? `Mappings — ${companyName}` : "Company Mappings"}
            </Text>
{companyMappings.some((m: any) => !m.isDefaultMapping) && (
              <Badge colorScheme="teal" fontSize="sm" px={3} py={1} borderRadius="full">
                {companyMappings.filter((m: any) => !m.isDefaultMapping).length} mapping{companyMappings.filter((m: any) => !m.isDefaultMapping).length > 1 ? "s" : ""}
              </Badge>
            )}
          </Flex>
          {mappingsLoading ? (
            <Flex justify="center" align="center" py={16}><Spinner size="lg" color="teal.500" /></Flex>
          ) : companyMappings.length > 0 ? (
            <TableContainer borderWidth={1} borderRadius="lg" borderColor="gray.200" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.600" py={3}>Rate Card Name</Th>
                    <Th color="gray.600" py={3}>Plan ID</Th>
                    <Th color="gray.600" py={3}>Min</Th>
                    <Th color="gray.600" py={3}>Max</Th>
                    <Th color="gray.600" py={3}>Count Scope</Th>
                    <Th color="gray.600" py={3}>Mapping</Th>
                    <Th color="gray.600" py={3}>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(companyMappings.some((m: any) => !m.isDefaultMapping)
                    ? companyMappings.filter((m: any) => !m.isDefaultMapping)
                    : companyMappings
                  ).map((mapping: any) => (
                    <Tr key={mapping._id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="semibold" color="gray.700">{mapping.rateCardName || "N/A"}</Td>
                      <Td>
                        {mapping.planId
                          ? <Badge colorScheme="blue" px={2} py={0.5} borderRadius="md" fontSize="xs">{mapping.planId}</Badge>
                          : <Text fontSize="xs" color="gray.400">-</Text>
                        }
                      </Td>
                      <Td>
                        {mapping.minCount !== undefined && mapping.minCount !== null
                          ? <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="xs">{mapping.minCount}</Badge>
                          : <Text fontSize="xs" color="gray.400">-</Text>
                        }
                      </Td>
                      <Td>
                        {mapping.maxCount !== undefined && mapping.maxCount !== null
                          ? <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="xs">{mapping.maxCount}</Badge>
                          : <Text fontSize="xs" color="gray.400">-</Text>
                        }
                      </Td>
                      <Td>
                        {mapping.countScope
                          ? <Badge colorScheme="cyan" px={2} py={0.5} borderRadius="md" fontSize="xs">{mapping.countScope}</Badge>
                          : <Text fontSize="xs" color="gray.400">-</Text>
                        }
                      </Td>
                      <Td>
                        {mapping.isGMC ? (
                          <Badge colorScheme="green" px={2} py={0.5} borderRadius="md" fontSize="xs">GMC</Badge>
                        ) : mapping.isDefaultMapping ? (
                          <Badge colorScheme="orange" px={2} py={0.5} borderRadius="md" fontSize="xs">★ Default</Badge>
                        ) : (
                          <Badge colorScheme="purple" px={2} py={0.5} borderRadius="md" fontSize="xs">Mapped</Badge>
                        )}
                      </Td>
                      <Td>
                        <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleDelete(mapping._id)} _hover={{ bg: "red.50" }}>
                          Delete
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Flex direction="column" align="center" justify="center" py={16}
              borderWidth={1} borderRadius="lg" borderColor="gray.200" borderStyle="dashed" bg="gray.50">
              <Text color="gray.400" fontSize="sm">
                {companyId ? "No mappings found for this company" : "Select a company to view its rate card mappings"}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Section 2: Default Rate Card */}
      <Box display="flex" w="100%" gap={6} alignItems="flex-start">
        <Box p={6} minW="320px" maxW="360px" bg="white" borderRadius="lg" boxShadow="md" border="1px solid" borderColor="orange.200">
          <HStack mb={1}>
            <Badge colorScheme="orange" fontSize="xs" px={2} py={0.5} borderRadius="md">Global</Badge>
          </HStack>
          <Text fontSize="lg" fontWeight="bold" mb={1} color="gray.700">Default Rate Card</Text>
          <Text fontSize="xs" color="gray.500" mb={5}>Applicable to all companies</Text>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm" color="gray.600">Rate Card</FormLabel>
              <Select
                placeholder="Select Rate Card" value={defaultRateCardId}
                onChange={(e) => setDefaultRateCardId(e.target.value)}
                borderColor="orange.300" _hover={{ borderColor: "orange.400" }} focusBorderColor="orange.400"
              >
                {rateCardOptions}
              </Select>
            </FormControl>
            <Button colorScheme="orange" onClick={handleSetDefault} isLoading={defaultLoading}>
              Set as Default
            </Button>
          </VStack>
        </Box>

        <Box flex={1} minW={0}>
          <Flex align="center" justify="space-between" mb={4}>
            <Text fontWeight="bold" fontSize="lg" color="gray.700">Default Rate Cards</Text>
            {defaultMappings.length > 0 && (
              <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full">
                {defaultMappings.length} default{defaultMappings.length > 1 ? "s" : ""}
              </Badge>
            )}
          </Flex>
          {defaultMappingsLoading ? (
            <Flex justify="center" align="center" py={16}><Spinner size="lg" color="orange.400" /></Flex>
          ) : defaultMappings.length > 0 ? (
            <TableContainer borderWidth={1} borderRadius="lg" borderColor="orange.200" boxShadow="sm">
              <Table variant="simple" size="sm">
                <Thead bg="orange.50">
                  <Tr>
                    <Th color="gray.600" py={3}>Rate Card Name</Th>
                    <Th color="gray.600" py={3}>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {defaultMappings.map((mapping: any) => (
                    <Tr key={mapping._id} _hover={{ bg: "orange.50" }}>
                      <Td fontWeight="semibold" color="gray.700">
                        <HStack>
                          <Text>{mapping.rateCardName || "N/A"}</Text>
                          <Badge colorScheme="orange" fontSize="xs" px={2} py={0.5} borderRadius="md">Default</Badge>
                        </HStack>
                      </Td>
                      <Td>
                        <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleDeleteDefault(mapping._id)} _hover={{ bg: "red.50" }}>
                          Delete
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Flex direction="column" align="center" justify="center" py={16}
              borderWidth={1} borderRadius="lg" borderColor="orange.100" borderStyle="dashed" bg="orange.50">
              <Text color="orange.300" fontSize="sm">No default rate cards set yet</Text>
            </Flex>
          )}
        </Box>
      </Box>

    </Box>
  )
}

export default RateCardMapping
